import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useRealtimeSubscription } from "@/hooks/useRealtimeSubscription";
import { authKeys, type Profile } from "@/hooks/useAuthQueries";
import { resolveActiveTeamId } from "@/lib/teamScope";

interface UserTeam {
  id: string;
  name: string;
}

const USER_TEAMS_KEY = ["userTeams"] as const;

/**
 * Hook to manage the active team for the current user.
 * Auto-selects if the user is in exactly one team.
 * Returns null if the user is in 0 teams or hasn't selected one.
 */
export function useActiveTeam() {
  const { user, profile } = useAuth();
  const queryClient = useQueryClient();

  // Realtime: invalidate when team_members or profiles change
  useRealtimeSubscription(["team_members", "teams", "profiles"], () => {
    queryClient.invalidateQueries({ queryKey: USER_TEAMS_KEY });
  });

  // Fetch user's teams
  const { data: userTeams = [], isLoading: teamsLoading } = useQuery({
    queryKey: USER_TEAMS_KEY,
    queryFn: async (): Promise<UserTeam[]> => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from("team_members")
        .select("team_id, teams:team_id(id, name)")
        .eq("user_id", user.id);
      if (error) throw error;
      return (data || [])
        .map((tm) => tm.teams as UserTeam | null)
        .filter((t: UserTeam | null): t is UserTeam => t !== null);
    },
    enabled: !!user?.id,
  });

  // Resolve active team ID
  const activeTeamId = resolveActiveTeamId(profile?.active_team_id ?? null, userTeams);
  const activeTeam = userTeams.find((t) => t.id === activeTeamId) ?? null;
  const needsTeamSelection = userTeams.length > 1 && !activeTeamId;

  // Mutation to set active team with optimistic update
  const setActiveTeamMutation = useMutation({
    mutationFn: async (teamId: string | null) => {
      if (!user?.id) throw new Error("Not authenticated");
      const { error } = await supabase
        .from("profiles")
        .update({ active_team_id: teamId })
        .eq("user_id", user.id);
      if (error) throw error;
    },
    onMutate: async (teamId: string | null) => {
      if (!user?.id) return;
      const profileKey = authKeys.profile(user.id);
      // Cancel in-flight fetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: profileKey });
      // Snapshot previous value for rollback
      const previousProfile = queryClient.getQueryData<Profile>(profileKey);
      // Optimistically update profile cache
      if (previousProfile) {
        queryClient.setQueryData<Profile>(profileKey, {
          ...previousProfile,
          active_team_id: teamId,
        });
      }
      return { previousProfile, profileKey };
    },
    onError: (_err, _teamId, context) => {
      // Rollback on error
      if (context?.previousProfile && context?.profileKey) {
        queryClient.setQueryData(context.profileKey, context.previousProfile);
      }
    },
    onSuccess: () => {
      if (!user?.id) return;
      // Invalidate profile to sync with server truth
      queryClient.invalidateQueries({ queryKey: authKeys.profile(user.id) });
      // Invalidate all team-dependent data
      queryClient.invalidateQueries({ queryKey: ["assignableProfiles"] });
      queryClient.invalidateQueries({ queryKey: ["inspections"] });
      queryClient.invalidateQueries({ queryKey: ["defects"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
  });

  return {
    /** All teams the user belongs to */
    userTeams,
    /** The currently active team (resolved) */
    activeTeam,
    /** The active team ID (resolved) */
    activeTeamId,
    /** Whether the user needs to select a team (multi-team, none selected) */
    needsTeamSelection,
    /** Whether the user is in multiple teams */
    isMultiTeam: userTeams.length > 1,
    /** Set the active team */
    setActiveTeam: (teamId: string | null) => setActiveTeamMutation.mutate(teamId),
    /** Loading state */
    isLoading: teamsLoading,
    /** Whether setting active team is in progress */
    isSetting: setActiveTeamMutation.isPending,
  };
}
